import { useState, useEffect } from "react";
import { UserPersona, TeamType } from "@/types/user";
import { useUser } from "@/contexts/UserContext";
import { useAuth } from "@/contexts/AuthContext";
import { redeemTeamInvitation } from "@/components/team/services/teamInviteService";
import { toast } from "sonner";
import { NotificationPreferencesData } from "@/components/onboarding/Steps/NotificationPreferencesStep";
import { onboardingService } from "@/services/onboardingService";

const VALID_PERSONAS: UserPersona[] = [
  "individual",
  "manager",
  "newJoiner",
  "newTeamMember",
  "admin",
  "organizationAdmin",
  "soloUser",
  "independentSolo",
  "organizationalSolo",
];

const isBrowser = typeof window !== "undefined";

const safeGetLocalStorage = (key: string): string | null => {
  if (!isBrowser) return null;
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn(`Failed to read localStorage key "${key}"`, error);
    }
    return null;
  }
};

const safeSetLocalStorage = (key: string, value: string) => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn(`Failed to write localStorage key "${key}"`, error);
    }
  }
};

const safeRemoveLocalStorage = (key: string) => {
  if (!isBrowser) return;
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn(`Failed to remove localStorage key "${key}"`, error);
    }
  }
};

const safeGetSessionStorage = (key: string): string | null => {
  if (!isBrowser) return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn(`Failed to read sessionStorage key "${key}"`, error);
    }
    return null;
  }
};

const safeRemoveSessionStorage = (key: string) => {
  if (!isBrowser) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn(`Failed to remove sessionStorage key "${key}"`, error);
    }
  }
};

const parseStoredPersona = (value: string | null, fallback: UserPersona): UserPersona => {
  if (!value) return fallback;
  return VALID_PERSONAS.includes(value as UserPersona) ? (value as UserPersona) : fallback;
};

interface InvitationParams {
  isInvitedUser?: boolean;
  invitedTeamId?: string;
  invitedTeamName?: string;
  invitedTeamType?: TeamType;
}

export interface OnboardingStepsHookReturn {
  currentStep: number;
  currentStepType: string;
  selectedPersona: UserPersona;
  selectedTeam: TeamType;
  selectedTeamName: string;
  selectedTeamId: string;
  selectedOrganizationId: string;
  selectedOrganizationName: string;
  selectedSkills: string[];
  selectedPlanId: string;
  hasCompletedPlanSelection: boolean;
  notificationPreferences: NotificationPreferencesData;
  handleStepComplete: () => void;
  handleStepBack: () => void;
  setSelectedPersona: (persona: UserPersona) => void;
  setSelectedTeam: (team: TeamType) => void;
  setSelectedTeamId: (id: string) => void;
  setSelectedTeamName: (name: string) => void;
  setSelectedOrganizationId: (id: string) => void;
  setSelectedOrganizationName: (name: string) => void;
  handleSkillToggle: (skill: string) => void;
  setSelectedPlanId: (planId: string) => void;
  setHasCompletedPlanSelection: (completed: boolean) => void;
  setNotificationPreferences: (prefs: NotificationPreferencesData) => void;
}

export function useOnboardingSteps(
  onComplete: () => void,
  updateUserProfile: (updates: any) => void,
  invitationParams?: InvitationParams
): OnboardingStepsHookReturn {
  // Persist onboarding progress in localStorage for resilience across sessions
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = safeGetLocalStorage('onboarding-current-step');
    const parsed = saved ? parseInt(saved, 10) : NaN;
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
  });
  const [selectedPersona, setSelectedPersona] = useState<UserPersona>(() => {
    const fallback = invitationParams?.isInvitedUser ? "newTeamMember" : "individual";
    const saved = safeGetLocalStorage('onboarding-selected-persona');
    return parseStoredPersona(saved, fallback);
  });
  const [selectedTeam, setSelectedTeam] = useState<TeamType>(
    invitationParams?.invitedTeamType || "general"
  );
  const [selectedTeamId, setSelectedTeamId] = useState<string>(
    invitationParams?.invitedTeamId || ""
  );
  const [selectedTeamName, setSelectedTeamName] = useState<string>(
    invitationParams?.invitedTeamName || "General"
  );
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>("");
  const [selectedOrganizationName, setSelectedOrganizationName] = useState<string>("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [hasCompletedPlanSelection, setHasCompletedPlanSelection] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferencesData>({
    emailNotifications: true,
    emailFrequency: 'daily',
    checkInNotifications: true,
    goalNotifications: true,
    chatNotifications: false,
    insightNotifications: true,
    teamNotifications: true,
  });
  const { user } = useUser();
  const { user: authUser } = useAuth();
  
  // Check if user already has an active subscription on mount
  // If so, auto-complete plan selection step
  useEffect(() => {
    if (authUser?.hasActiveSubscription && !hasCompletedPlanSelection) {
      console.log('[useOnboardingSteps] User has active subscription, skipping plan selection');
      setHasCompletedPlanSelection(true);
      setSelectedPlanId(authUser.subscription?.planName || 'active');
    }
  }, [authUser?.hasActiveSubscription, authUser?.subscription?.planName, hasCompletedPlanSelection]);

  // Save step progress to localStorage whenever it changes
  useEffect(() => {
    safeSetLocalStorage('onboarding-current-step', currentStep.toString());
  }, [currentStep]);
  
  // Persist selected persona to prevent step calculation changes
  useEffect(() => {
    safeSetLocalStorage('onboarding-selected-persona', selectedPersona);
  }, [selectedPersona]);


  const handleStepComplete = async () => {
    const isInvitedUser = invitationParams?.isInvitedUser;
    
    // Calculate max step dynamically based on flow
    const needsOrganizationStep = !isInvitedUser && selectedPersona !== "independentSolo";
    const maxStep = isInvitedUser ? 4 : (
      1 + // Role selection
      (needsOrganizationStep ? 1 : 0) + // Organization selection
      1 + // Team/Functional area selection
      5   // Skills, Profile, Plan Selection, Notifications, Calendar
    );
    
    if (currentStep < maxStep) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
    } else {
      // Final step - complete onboarding
      try {
        let teamJoinResult = null;

        // If this is an invited user, process the invitation first
        if (isInvitedUser && invitationParams?.invitedTeamId) {
          const storedInvitation = safeGetLocalStorage('invitationData') ?? safeGetSessionStorage('invitationData');
          if (storedInvitation) {
            const invitationData = JSON.parse(storedInvitation);
            
            // Process the invitation (this will add them to the team)
            teamJoinResult = await redeemTeamInvitation(
              invitationData.inviteCode,
              user.id
            );

            if (teamJoinResult.success) {
              toast.success(`Successfully joined ${teamJoinResult.teamName}!`);
              // Clean up invitation data
              safeRemoveLocalStorage('invitationData');
              safeRemoveSessionStorage('invitationData');
            } else {
              console.error('Failed to redeem invitation:', teamJoinResult.error);
              toast.error('Failed to join team. Please contact your administrator.');
              return; // Don't continue if invitation redemption failed
            }
          } else {
            toast.error('Invitation data not found. Please try the invite link again.');
            return;
          }
        }

        // Get avatar URL from sessionStorage if user uploaded one during onboarding
        const savedAvatarUrl = safeGetSessionStorage('onboarding-avatar-url');

        // Determine teamId and teamName for invited users
        let finalTeamId = selectedTeamId;
        let finalTeamName = selectedTeamName;
        
        if (isInvitedUser && teamJoinResult?.success) {
          finalTeamId = teamJoinResult.teamId;
          finalTeamName = teamJoinResult.teamName;
        }

        // Complete onboarding using backend API - ALL BUSINESS LOGIC IN BACKEND
        // Backend will handle:
        // - Determining isAdmin based on persona
        // - Determining organizationRole based on persona
        // - Mapping profile fields based on persona type
        // - Creating teams if needed
        // - Adding users to teams
        // - Updating profile
        // - Saving notification preferences
        const result = await onboardingService.completeOnboarding({
          persona: selectedPersona,
          skills: selectedSkills,
          teamId: finalTeamId || null,
          teamName: finalTeamName || null,
          teamType: selectedTeam,
          organizationId: selectedOrganizationId || null,
          organizationName: selectedOrganizationName || null,
          avatarUrl: savedAvatarUrl || null,
          notificationPreferences: {
            emailNotifications: notificationPreferences.emailNotifications,
            emailFrequency: notificationPreferences.emailFrequency,
            checkInNotifications: notificationPreferences.checkInNotifications,
            goalNotifications: notificationPreferences.goalNotifications,
            chatNotifications: notificationPreferences.chatNotifications,
            insightNotifications: notificationPreferences.insightNotifications,
            teamNotifications: notificationPreferences.teamNotifications,
          },
        });

        // Update local user profile with response from backend
        if (result.data?.profile) {
          updateUserProfile(result.data.profile);
        }

    // Clear onboarding progress from localStorage since we're done
    safeRemoveLocalStorage('onboarding-current-step');
    safeRemoveLocalStorage('onboarding-selected-persona');
    safeRemoveLocalStorage('onboarding-avatar-url');
    // Also clear any session-based onboarding flags
    safeRemoveSessionStorage('onboarding-in-progress');
    safeRemoveSessionStorage('onboarding-plan-selected');
    safeRemoveSessionStorage('onboarding-plan-completed');
        onComplete();
      } catch (error) {
        console.error('Error completing onboarding:', error);
        toast.error('Failed to complete onboarding. Please try again.');
        // Reset to previous step instead of staying on broken final step
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const handleStepBack = () => {
    if (currentStep > 1) {
      const isInvitedUser = invitationParams?.isInvitedUser;
      
      // For invited users, simple decrement
      if (isInvitedUser) {
        setCurrentStep(currentStep - 1);
        return;
      }
      
      // For regular users, we need to handle skipped steps
      // Map current step to what it represents based on persona
      const needsOrganizationStep = selectedPersona !== "independentSolo";
      
      // Calculate which content is shown at each step number
      let stepMapping: { [key: number]: string } = {
        1: 'role'
      };
      
      let nextStepNum = 2;
      
      if (needsOrganizationStep) {
        stepMapping[nextStepNum] = 'organization';
        nextStepNum++;
      }
      
      stepMapping[nextStepNum] = 'team';
      nextStepNum++;
      
      stepMapping[nextStepNum] = 'skills';
      nextStepNum++;
      
      stepMapping[nextStepNum] = 'profile';
      nextStepNum++;
      
      stepMapping[nextStepNum] = 'notifications';
      nextStepNum++;
      
      stepMapping[nextStepNum] = 'calendar';
      
      // Now we can properly go back
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkillToggle = (skill: string) => {
    if (selectedSkills.includes(skill)) {
      setSelectedSkills(selectedSkills.filter((s) => s !== skill));
    } else {
      setSelectedSkills([...selectedSkills, skill]);
    }
  };

  return {
    currentStep,
    currentStepType: '', // Add empty string for now to maintain interface
    selectedPersona,
    selectedTeam,
    selectedTeamName,
    selectedTeamId,
    selectedOrganizationId,
    selectedOrganizationName,
    selectedSkills,
    selectedPlanId,
    hasCompletedPlanSelection,
    notificationPreferences,
    handleStepComplete,
    handleStepBack,
    setSelectedPersona,
    setSelectedTeam,
    setSelectedTeamId,
    setSelectedTeamName,
    setSelectedOrganizationId,
    setSelectedOrganizationName,
    handleSkillToggle,
    setSelectedPlanId,
    setHasCompletedPlanSelection,
    setNotificationPreferences,
  };
}
